import type { PreloadedQuery } from 'react-relay';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { JiraNavigationSettingsAri } from '@atlassian/ari/jira/navigation-settings';
import { JiraWorkspaceAri } from '@atlassian/ari/jira/workspace';
import {
	buildNavigationAriForMenuData,
	buildNavigationAriForSidebarData,
} from '@atlassian/global-side-navigation/navigation-ari';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type { RouteResource } from '@atlassian/react-resource-router';
import GSNQuery, {
	type GSNQueryPreloaderQuery,
} from '@jira/nav-platform-integration__sidebar/src/__generated__/GSNQueryPreloaderQuery.graphql.ts';
import {
	MENU_ID_APPS,
	MENU_ID_FILTERS,
	MENU_ID_OPERATIONS,
	MENU_ID_PLANS,
	MENU_ID_PROJECTS,
} from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { getInitialSelectedPath } from '@jira/navigation-apps__sidebar-nav4-context/src/common/utils/selected-path/getInitialSelectedPath.tsx';
import { isSelectedPath } from '@jira/navigation-apps__sidebar-nav4-context/src/common/utils/selected-path/isSelectedPath.tsx';
import { createRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';

const RESOURCE_TYPE_SIDEBAR_NAV_PLATFORM = 'RESOURCE_TYPE_SIDEBAR_NAV_PLATFORM';

export const navPlatformSidebarResource: RouteResource<
	PreloadedQuery<GSNQueryPreloaderQuery, Record<string, unknown>>
> = createRelayResource<GSNQueryPreloaderQuery>({
	type: RESOURCE_TYPE_SIDEBAR_NAV_PLATFORM,
	// Anonymous users must not trigger this GSN GraphQL query. Use the
	// resource-level `skip` predicate so `loadQuery` is never invoked for them —
	// `getQuery` will not run when `skip` returns `true`.
	skip: (_, { tenantContext: { isAnonymous } }) => {
		return isAnonymous;
	},
	getQuery: (routerContext, { tenantContext: { activationId, atlassianAccountId, cloudId } }) => {
		const initialSelectedPath = getInitialSelectedPath(routerContext);
		const ownerAri =
			atlassianAccountId && IdentityUserAri.create({ userId: atlassianAccountId }).toString();
		const fetchPolicy = 'store-or-network' as const;
		// Fetch the productionisation recommendations (`growthRecommendations`
		// behind `@include(if: $shouldFetchInvitedAppShortcuts)`) only for the test
		// cohort. Read via `UNSAFE_noExposureExp` so control skips the field without
		// firing an exposure here — exposure fires at render once recs show.
		const shouldFetchInvitedAppShortcuts =
			fg('twcg-460-preload-grs-invited-app-shortcuts-gate') &&
			UNSAFE_noExposureExp('twcg-460-preload-grs-invited-app-shortcuts')[0].get('isEnabled', false);
		return {
			parameters: GSNQuery,
			variables: {
				cloudId,
				...(fg('sea-4270-current-url-in-gsn')
					? {
							currentURL: new URL(
								`http://example.com${routerContext.match.url}/?${new URLSearchParams(
									routerContext.query ?? {},
								).toString()}`,
							).toString(),
						}
					: {}),
				entityAriApps: JiraNavigationSettingsAri.create({
					siteId: cloudId,
					activationId,
					navigationSettingScope: 'apps',
				}).toString(),
				entityAriFilters: JiraNavigationSettingsAri.create({
					siteId: cloudId,
					activationId,
					navigationSettingScope: 'filters',
				}).toString(),
				entityAriPlans: JiraNavigationSettingsAri.create({
					siteId: cloudId,
					activationId,
					navigationSettingScope: 'plans',
				}).toString(),
				...(fg('jpd-enable-roadmaps-recents-fe')
					? {
							entityAriRoadmaps: JiraNavigationSettingsAri.create({
								siteId: cloudId,
								activationId,
								navigationSettingScope: 'collections',
							}).toString(),
						}
					: {}),
				entityAriUserSideNavSettings: JiraWorkspaceAri.create({
					siteId: cloudId,
					activationId,
				}).toString(),
				isAppsExpanded: isSelectedPath(initialSelectedPath, MENU_ID_APPS),
				isFiltersExpanded: isSelectedPath(initialSelectedPath, MENU_ID_FILTERS),
				isPlansExpanded: isSelectedPath(initialSelectedPath, MENU_ID_PLANS),
				isProjectsExpanded: isSelectedPath(initialSelectedPath, MENU_ID_PROJECTS),
				isOperationsExpanded: isSelectedPath(initialSelectedPath, MENU_ID_OPERATIONS),
				navPlatMenuDataEntityAri: buildNavigationAriForMenuData({
					activationId,
					firstPartyApp: 'jira',
					siteId: cloudId,
				}),
				navPlatSidebarDataEntityAri: buildNavigationAriForSidebarData({
					activationId,
					firstPartyApp: 'jira',
					siteId: cloudId,
				}),
				ownerAri,
				fetchGlobalShortcutsAvailableProducts: Boolean(
					cloudId && ownerAri && fg('navx-4515-jira-gsn-gas-ssr'),
				),
				isItamEnabled: Boolean(fg('itam_ftw')),
				itamStaticChecks: fg('itam_ftw')
					? [
							{
								resourceId: `ari:cloud:jira-servicedesk::site/${cloudId}`,
								permissionId: 'write',
							},
							{
								resourceId: `ari:cloud:jira::site/${cloudId}`,
								permissionId: 'manage',
							},
							{
								resourceId: `ari:cloud:cmdb::site/${cloudId}`,
								permissionId: 'manage',
							},
						]
					: [],
				shouldFetchInvitedAppShortcuts,
				invitedAppShortcutsProduct: 'jira',
			},
			options: { fetchPolicy },
		};
	},
	isCritical: true,
});
